import { api } from "utils/api";

const SUPPORTED_RESOLUTIONS = ["1", "5", "15", "30", "60", "240", "1D", "1W", "1M"];

const RESOLUTION_TO_SEC = {
  "1": 60,
  "5": 300,
  "15": 900,
  "30": 1800,
  "60": 3600,
  "240": 14400,
  D: 86400,
  "1D": 86400,
};

// get-ohlc-history returns the exchange clock: CandleDate is an IST calendar day
// and CandleTime is an "HH:mm" IST wall-clock string (the backend keeps OHLC in
// IST — see OhlcAutoSaveHostedService's minuteIst/candleTimeIst). Asia/Kolkata
// has no DST, so a fixed offset converts that wall clock to a real instant.
const IST_OFFSET_MS = 5.5 * 60 * 60 * 1000;

/** IST calendar day (YYYY-MM-DD) — kept for any tooling that needs wall-clock dates. */
function istYmd(ms) {
  return new Date(ms + IST_OFFSET_MS).toISOString().slice(0, 10);
}

/**
 * Bucket boundary for a real instant, aligned to the exchange (IST) clock
 * rather than to UTC — otherwise hourly and daily bars would start on IST
 * :30 boundaries, since IST is offset by half an hour.
 */
function bucketStartMs(timeMs, resolution, resolutionSec) {
  const shifted = timeMs + IST_OFFSET_MS;

  if (resolution === "1M" || resolution === "M") {
    const d = new Date(shifted);
    return Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), 1) - IST_OFFSET_MS;
  }
  if (resolution === "1W" || resolution === "W") {
    const d = new Date(shifted);
    const daysSinceMonday = (d.getUTCDay() + 6) % 7;
    return (
      Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate() - daysSinceMonday) -
      IST_OFFSET_MS
    );
  }

  const bucketMs = resolutionSec * 1000;
  return Math.floor(shifted / bucketMs) * bucketMs - IST_OFFSET_MS;
}

function parseHistoryBody(body) {
  if (!body) return [];
  if (Array.isArray(body)) return body;
  if (Array.isArray(body.data)) return body.data;
  if (Array.isArray(body.Data)) return body.Data;
  if (Array.isArray(body.records)) return body.records;
  if (Array.isArray(body.result)) return body.result;
  if (Array.isArray(body.items)) return body.items;
  if (Array.isArray(body?.data?.data)) return body.data.data;
  return [];
}

function n(v) {
  const x = Number(v);
  return Number.isFinite(x) ? x : null;
}

function parseExchangeDateTimeToMs(candleDateRaw, candleTimeRaw) {
  const dRaw = String(candleDateRaw ?? "").trim();
  const tRaw = String(candleTimeRaw ?? "").trim();
  if (!dRaw || !tRaw) return null;
  const d = dRaw.includes("T") ? dRaw.split("T")[0] : dRaw;
  const tm = tRaw.length > 5 ? tRaw.slice(0, 5) : tRaw;
  const dp = d.split("-");
  const tp = tm.split(":");
  if (dp.length !== 3 || tp.length < 2) return null;
  const y = Number(dp[0]), m = Number(dp[1]), day = Number(dp[2]);
  const hh = Number(tp[0]), mm = Number(tp[1]);
  if (![y, m, day, hh, mm].every(Number.isFinite)) return null;
  // The old lightweight-charts view fed the IST wall clock straight through as
  // if it were UTC, because that library plots timestamps in UTC. TradingView
  // renders in the configured timezone instead, so the wall clock has to become
  // a real instant here or every bar lands 5h30m late.
  return Date.UTC(y, m - 1, day, hh, mm, 0) - IST_OFFSET_MS;
}

function toEpochMs(v) {
  if (v == null || v === "") return null;
  if (typeof v === "number" && Number.isFinite(v))
    return v > 1e12 ? Math.floor(v) : Math.floor(v * 1000);
  if (typeof v === "string") {
    const asNum = Number(v.trim());
    if (Number.isFinite(asNum))
      return asNum > 1e12 ? Math.floor(asNum) : Math.floor(asNum * 1000);
    const ms = Date.parse(v.trim());
    if (Number.isFinite(ms)) return ms;
  }
  return null;
}

function rowToBar(r) {
  const open = n(r.open ?? r.Open ?? r.OpenPrice ?? r.openPrice);
  const high = n(r.high ?? r.High ?? r.HighPrice ?? r.highPrice);
  const low = n(r.low ?? r.Low ?? r.LowPrice ?? r.lowPrice);
  const close = n(r.close ?? r.Close ?? r.ClosePrice ?? r.closePrice);
  const volume = Math.max(0, n(r.volume ?? r.Volume) ?? 0);

  let time =
    parseExchangeDateTimeToMs(r.CandleDate ?? r.candleDate, r.CandleTime ?? r.candleTime) ??
    toEpochMs(r.time) ??
    toEpochMs(r.Time) ??
    toEpochMs(r.timestamp) ??
    toEpochMs(r.Timestamp);

  if (!time || [open, high, low, close].some((v) => v == null)) return null;
  return { time, open, high, low, close, volume };
}

function aggregateBars(bars, resolution, resolutionSec) {
  const isCalendarBucket = ["1W", "W", "1M", "M"].includes(resolution);
  if (!isCalendarBucket && (!resolutionSec || resolutionSec <= 60)) return bars;
  const out = [];
  let cur = null;
  for (const b of bars) {
    const bt = bucketStartMs(b.time, resolution, resolutionSec);
    if (!cur || cur.time !== bt) {
      if (cur) out.push(cur);
      cur = { time: bt, open: b.open, high: b.high, low: b.low, close: b.close, volume: b.volume };
    } else {
      cur.high = Math.max(cur.high, b.high);
      cur.low = Math.min(cur.low, b.low);
      cur.close = b.close;
      cur.volume += b.volume;
    }
  }
  if (cur) out.push(cur);
  return out;
}

export default function createDatafeed(getSymbolKey) {
  const lastBarCache = {};
  /** Full 1m history per symbol (API called without from/to/maxRows). */
  const historyCache = {};
  const subscribers = {};

  return {
    onReady(callback) {
      setTimeout(() => {
        callback({
          supported_resolutions: SUPPORTED_RESOLUTIONS,
          supports_marks: false,
          supports_timescale_marks: false,
          supports_time: true,
        });
      }, 0);
    },

    searchSymbols(_input, _exchange, _type, onResult) {
      onResult([]);
    },

    resolveSymbol(symbolName, onResolve, _onError) {
      setTimeout(() => {
        onResolve({
          name: symbolName,
          full_name: symbolName,
          description: symbolName,
          type: "stock",
          session: "0915-1530",
          timezone: "Asia/Kolkata",
          ticker: symbolName,
          exchange: "",
          listed_exchange: "",
          minmov: 1,
          pricescale: 100,
          has_intraday: true,
          has_daily: true,
          // The feed only stores 1-minute candles; the library aggregates the
          // higher intraday resolutions itself, session-aware.
          intraday_multipliers: ["1"],
          has_weekly_and_monthly: false,
          supported_resolutions: SUPPORTED_RESOLUTIONS,
          volume_precision: 0,
          data_status: "streaming",
        });
      }, 0);
    },

    async getBars(symbolInfo, resolution, periodParams, onResult, onError) {
      const { from, to, firstDataRequest } = periodParams;
      const sym = getSymbolKey ? getSymbolKey() : symbolInfo.name;
      const resSec = RESOLUTION_TO_SEC[resolution] || 60;

      try {
        // Load full symbol history once (no from/to/maxRows on API).
        let bars1m = historyCache[sym];
        if (!bars1m) {
          const res = await api.get("/market-watch/get-ohlc-history", {
            params: { symbol: sym },
          });
          const raw = parseHistoryBody(res?.data);
          bars1m = raw.map(rowToBar).filter(Boolean).sort((a, b) => a.time - b.time);
          historyCache[sym] = bars1m;
        }

        if (!bars1m.length) {
          onResult([], { noData: true });
          return;
        }

        const fromMs = from * 1000;
        const toMs = to * 1000;
        const oldestMs = bars1m[0].time;
        const newestMs = bars1m[bars1m.length - 1].time;

        // Request entirely before our oldest bar → stop history paging (avoids infinite getBars).
        if (toMs < oldestMs) {
          onResult([], { noData: true });
          return;
        }

        // First paint: full series so older days show. Later: only bars in TradingView's from/to.
        let slice;
        if (firstDataRequest) {
          slice = bars1m;
        } else {
          slice = bars1m.filter((b) => b.time >= fromMs && b.time <= toMs);
          // Clamp left edge: include bars that overlap the left of the requested window.
          if (!slice.length && fromMs <= newestMs && toMs >= oldestMs) {
            slice = bars1m.filter((b) => b.time <= toMs && b.time >= Math.min(fromMs, oldestMs));
          }
        }

        if (!slice.length) {
          onResult([], { noData: true });
          return;
        }

        const bars = aggregateBars(slice, resolution, resSec);
        if (!bars.length) {
          onResult([], { noData: true });
          return;
        }

        if (firstDataRequest) {
          lastBarCache[symbolInfo.name] = bars[bars.length - 1];
        }

        // Full history is already in cache. firstDataRequest returns the whole series
        // with noData:true so TV does not keep paging left (that loop caused stack overflow).
        // Later requests only return the from/to slice; empty / past-oldest → noData above.
        const noMoreHistory = firstDataRequest || bars[0].time <= oldestMs;
        onResult(bars, { noData: noMoreHistory });
      } catch (e) {
        onError(e?.message || "Failed to load data");
      }
    },

    subscribeBars(symbolInfo, resolution, onTick, listenerGuid) {
      const resSec = RESOLUTION_TO_SEC[resolution] || 60;
      subscribers[listenerGuid] = {
        symbolName: symbolInfo.name,
        resolution,
        resSec,
        onTick,
      };
    },

    unsubscribeBars(listenerGuid) {
      delete subscribers[listenerGuid];
    },

    /**
     * Called externally by TvChartWidget when a live tick arrives from Redux.
     * Pushes a real-time bar update into every active subscriber.
     */
    pushLiveTick(symbolName, ltp, volume) {
      if (!Number.isFinite(ltp) || ltp <= 0) return;

      for (const sub of Object.values(subscribers)) {
        if (sub.symbolName !== symbolName) continue;

        // Real instant: history bars are real instants too, so a live tick must
        // not be shifted into the exchange wall clock.
        const bucketTime = bucketStartMs(Date.now(), sub.resolution, sub.resSec);

        const last = lastBarCache[symbolName];

        let bar;
        if (!last || last.time < bucketTime) {
          const open = last?.close ?? ltp;
          bar = {
            time: bucketTime,
            open,
            high: Math.max(open, ltp),
            low: Math.min(open, ltp),
            close: ltp,
            volume: volume ?? 0,
          };
        } else {
          bar = {
            time: last.time,
            open: last.open,
            high: Math.max(last.high, ltp),
            low: Math.min(last.low, ltp),
            close: ltp,
            volume: volume ?? last.volume ?? 0,
          };
        }

        lastBarCache[symbolName] = bar;
        try { sub.onTick(bar); } catch {}
      }
    },
  };
}
